import type {Price} from '@/redux/v1/models';

import {Slider, SliderLabels} from '@travel-frontend/ui-components';
import {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {SliderLabel} from '@/components/SliderLabel';
import {config} from '@/config';
import {FilterTile} from '@/modules/common/components/Filter/components/FilterTile';
import {createSliderValues} from '@/modules/common/components/Filter/utils/createSliderValues';
import {findBestMatch} from '@/modules/common/components/Filter/utils/findBestMatch';
import {priceFormat} from '@/utils/numberFormat';

type Value = [Price | null, Price | null];

type Props = {
  caption: React.ReactNode;
  value: Value;
  priceMin?: null | Price;
  onChange: (param: {type: 'clear' | 'set'; value?: Value}) => void;
};

const sliderValues = createSliderValues(config.filters.price.ranges);
const sliderValueMax = sliderValues[sliderValues.length - 1] as number;
const sliderValuesLastIndex = sliderValues.length - 1;

export const PriceFilter: React.FunctionComponent<Props> = ({
  value,
  priceMin,
  onChange,
  ...restProps
}) => {
  const {t} = useTranslation('general');

  const [sliderValueIndex, setSliderValueIndex] = useState<number>(() =>
    findBestMatch(value[1]?.amount, sliderValues)
  );

  useEffect(() => {
    const index = findBestMatch(value[1]?.amount, sliderValues);
    setSliderValueIndex(index);
  }, [value]);

  const sliderLabel =
    sliderValues[sliderValueIndex] === sliderValueMax ? (
      priceFormat(
        {
          amount: sliderValueMax,
          currency: import.meta.env.VITE_PORTAL_CURRENCY,
        },
        {
          fractionDigits: 0,
        }
      ).replace(/(\d)(?!.*\d)/u, '$1+')
    ) : (
      <Trans
        i18nKey="general:priceUntil"
        values={{
          price: sliderValues[sliderValueIndex],
        }}
        components={[<span key="priceFilterSliderSpan" />]}
        tOptions={{
          formatParams: {
            price: {
              currency: import.meta.env.VITE_PORTAL_CURRENCY,
            },
          },
        }}
      />
    );

  return (
    <FilterTile {...restProps}>
      <Slider
        min={0}
        max={sliderValuesLastIndex}
        value={sliderValueIndex}
        onSlide={(n) => {
          if (typeof n === 'number') {
            setSliderValueIndex(n);
          }
        }}
        onChange={(n) => {
          if (typeof n === 'number') {
            if (n === sliderValues.length - 1 && value != null) {
              onChange({type: 'clear', value: [null, null]});
            } else if (
              sliderValues[n] !== value[1]?.amount &&
              n !== sliderValues.length - 1
            ) {
              onChange({
                type: 'set',
                value: [
                  null,
                  {
                    amount: sliderValues[n],
                    currency: import.meta.env.VITE_PORTAL_CURRENCY,
                  },
                ],
              });
            }
          }
        }}
      />
      <SliderLabels>
        {priceMin ? (
          <SliderLabel>
            {t('general:bestPrice', {
              price: priceMin.amount,
              currency: priceMin.currency,
            })}
          </SliderLabel>
        ) : (
          <div />
        )}
        <SliderLabel highlighted={value[1] != null}>{sliderLabel}</SliderLabel>
      </SliderLabels>
    </FilterTile>
  );
};
